import React from "react";
import { Link } from "react-router-dom";
import { Calculator } from "lucide-react";
import { Caption } from "@/components/ui/typography";

// ============================================================
// FYUNOR — Footer
// Four focused columns: Calculators, Resources, Company, Connect.
// Curated and trustworthy — only live links or clearly marked
// "Coming Soon" items.
// ============================================================

const COLUMNS = [
  {
    title: "Financial Calculators",
    links: [
      { label: "US Salary Calculator", to: "/us/calculators/salary" },
      { label: "US Mortgage Calculator", to: "/us/calculators/mortgage" },
      { label: "US Loan Calculator", to: "/us/calculators/loan" },
      { label: "Compound Interest Calculator", to: "/calculators/compound-interest" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "US Salary Guide 2026", to: "/guides/us-salary-2026" },
      { label: "Compound Interest Guide", to: "/guides/compound-interest" },
      { label: "Compound Interest Formula", to: "/guides/compound-interest-formula" },
      { label: "Compound Interest Table", to: "/guides/compound-interest-table" },
      { label: "Investing $500 a Month", to: "/guides/investing-500-a-month" },
      { label: "Methodology", to: "/methodology" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Terms of Use", to: "/terms" },
      { label: "Disclaimer", to: "/disclaimer" },
    ],
  },
];

const BOTTOM_LINKS = [
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms", to: "/terms" },
  { label: "Disclaimer", to: "/disclaimer" },
];

export default function Footer() {
  return (
    <footer className="mt-xxl border-t border-border bg-surface/50">
      <div className="container-fyunor py-16">
        {/* Brand */}
        <div className="flex items-center gap-2.5">
          <span className="flex size-7 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Calculator className="size-[15px]" aria-hidden="true" />
          </span>
          <div>
            <div className="font-display text-base font-bold tracking-[var(--tracking-display)] text-foreground">Fyunor</div>
            <Caption>Make Better Financial Decisions</Caption>
          </div>
        </div>

        {/* Three columns — full width, evenly distributed */}
        <div className="mt-12 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-16">
          {COLUMNS.map((column) => (
            <div key={column.title}>
              <h2 className="font-heading text-sm font-semibold text-foreground">{column.title}</h2>
              <ul className="mt-6 space-y-4">
                {column.links.map((link) => (
                  <li key={link.label}>
                    {link.to ? (
                      <Link
                        to={link.to}
                        className="text-sm text-muted-foreground transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                      >
                        {link.label}
                      </Link>
                    ) : link.comingSoon ? (
                      <span className="inline-flex items-center gap-2 text-sm text-subtle-foreground">
                        {link.label}
                        <span className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-subtle-foreground">
                          Soon
                        </span>
                      </span>
                    ) : (
                      <span className="text-sm text-subtle-foreground">{link.label}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="mt-12 border-t border-border pt-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="text-xs text-subtle-foreground">© {new Date().getFullYear()} Fyunor</div>
            <nav className="flex items-center gap-3">
              {BOTTOM_LINKS.map((link, i) => (
                <React.Fragment key={link.label}>
                  {i > 0 && <span className="text-subtle-foreground">•</span>}
                  <Link
                    to={link.to}
                    className="text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </Link>
                </React.Fragment>
              ))}
            </nav>
          </div>
          <p className="mt-4 max-w-3xl text-xs leading-relaxed text-subtle-foreground">
            Fyunor provides free financial calculators for educational purposes only. The information on this
            website does not constitute financial, tax or legal advice.
          </p>
        </div>
      </div>
    </footer>
  );
}